import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BellRing, Play } from "lucide-react";

function labelFor(f) {
  const bits = [];
  if (f.bhk) bits.push(`${f.bhk} BHK`);
  bits.push(f.property_type || "properties");
  if (f.listing_type === "rent") bits.push("to rent");
  else if (f.listing_type === "sale") bits.push("for sale");
  const loc = [f.locality, f.city].filter(Boolean).join(", ");
  if (loc) bits.push(`in ${loc}`);
  return bits.join(" ");
}

/**
 * Admin oversight for the "Save search & get alerts" feature — shows every
 * saved search (active + unsubscribed) and lets admin trigger the daily
 * matching job on demand instead of waiting for the scheduler.
 */
export default function SavedSearchAlertsAdminTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/saved-searches").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const activeCount = items.filter((i) => i.active).length;

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/saved-searches/run-now");
      toast.success(`Sent ${data.alerts_sent} alert email(s), ${data.no_new_matches} had no new matches.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="bg-white border border-brand-subtle rounded-2xl p-6" data-testid="saved-search-alerts-admin">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#FBF6EC] flex items-center justify-center text-[#7A1E2A]"><BellRing size={20} /></div>
          <div>
            <p className="label-eyebrow text-gray-500">Locality alerts</p>
            <h3 className="font-display text-xl brand-forest">Saved search alerts ({activeCount} active)</h3>
          </div>
        </div>
        <Button
          data-testid="saved-search-run-now"
          onClick={runNow}
          disabled={running}
          className="rounded-full h-10 px-4 bg-[#7A1E2A]/10 text-[#7A1E2A] border border-[#7A1E2A]/25 hover:bg-[#7A1E2A]/20"
        >
          <Play size={13} className="mr-1.5" /> {running ? "Running…" : "Run matching now"}
        </Button>
      </div>
      <p className="text-xs text-gray-500 mb-4">Runs automatically once a day; use "Run matching now" to test immediately.</p>
      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">No saved searches yet.</p>
      ) : (
        <div className="max-h-80 overflow-y-auto divide-y divide-brand-subtle">
          {items.map((s) => (
            <div key={s.id} data-testid={`admin-saved-search-${s.id}`} className="py-2.5 flex items-center justify-between gap-3 text-sm">
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 truncate">{s.email}</p>
                <p className="text-xs text-gray-500 capitalize truncate">{labelFor(s.filters)}</p>
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full shrink-0 ${s.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                {s.active ? "Active" : "Unsubscribed"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
